import { Component, input, signal, computed, ViewChild, ElementRef, effect, EffectRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { JsonTreeComponent } from '../json-tree/json-tree.component';
import { StateSnapshot } from '../../../core/interfaces/state.interface';
import { Constants, ScreenRenderingMode, TabCoreScreens } from '../../../core/const/constants';
import { TabFormRendererComponent } from '../../../features-modules/formio/tab-form-renderer/tab-form-renderer.component';
import { SplitterModule } from '@syncfusion/ej2-angular-layouts';
import { TreeItem } from '../../../core/interfaces/dev-tool/state.interface';

@Component({
  selector: 'app-state-view',
  standalone: true,
  imports: [CommonModule, JsonTreeComponent, TabFormRendererComponent, SplitterModule],
  templateUrl: './state-view.component.html',
  styleUrls: ['./state-view.component.scss'],
})
export class StateViewComponent implements OnDestroy {
  state = input<StateSnapshot | null>(null);
  contentHeight = input<number>(500);

  selectedPath = signal<string | null>(null);
  searchQuery = signal('');
  jsonSearchQuery = signal('');
  showJsonSearch = signal(false);

  // Track expanded state for tree items
  expandedItems = signal<Set<string>>(new Set());

  private jsonSearchTimeout: any = null;
  private timeouts: any[] = [];

  activeTab = signal<'json' | 'edit' | 'run'>('json');

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('editRenderer') editRenderer: any;
  @ViewChild('runRenderer') runRenderer: any;
  @ViewChild('scriptEditRenderer') scriptEditRenderer: any;

  private focusEffectRef?: EffectRef;

  constructor() {
    this.focusEffectRef = effect(() => {
      if (this.showJsonSearch()) {
        setTimeout(() => {
          this.searchInput?.nativeElement?.focus();
        }, 100);
      }
    });
  }

  setJsonSearchQuery(value: string) {
    if (this.jsonSearchTimeout) {
      clearTimeout(this.jsonSearchTimeout);
    }
    this.jsonSearchTimeout = setTimeout(() => {
      this.jsonSearchQuery.set(value);
    }, 500);
  }

  toggleJsonSearch() {
    const newValue = !this.showJsonSearch();
    this.showJsonSearch.set(newValue);
    if (!newValue) {
      this.jsonSearchQuery.set('');
    }
  }

  // Toggle expansion for tree items
  toggleExpand(path: string, event?: Event) {
    if (event) {
      event.stopPropagation(); // Prevent selection when clicking arrow
    }
    const expanded = this.expandedItems();
    const newExpanded = new Set(expanded);

    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }

    this.expandedItems.set(newExpanded);
  }

  isExpanded(path: string): boolean {
    return this.expandedItems().has(path);
  }

  // Handle row click for different item types
  handleRowClick(item: TreeItem) {
    if (item.type === 'parent-item') {
      // Screen row: toggle expand AND select
      this.toggleExpand(item.path);
      this.selectItem(item.path);
    } else if (item.type === 'parent-group') {
      // Group header: only toggle expand (not selectable)
      this.toggleExpand(item.path);
    } else if (item.type === 'leaf-item') {
      // Leaf item: only select (not expandable)
      this.selectItem(item.path);
    }
  }

  // Build tree structure
  treeItems = computed(() => {
    const currentState = this.state();
    if (!currentState) return [];

    const tree: TreeItem[] = [];
    const searchTerm = this.searchQuery().toLowerCase().trim();

    // ========================================
    // GLOBAL SECTION (Remove Screens subsections)
    // ========================================
    const globals = currentState.global?.globals || {};
    const hasGlobals = Object.keys(globals).length > 0;

    // Collect matching global items first
    const matchingGlobalItems: TreeItem[] = [];

    if (hasGlobals || currentState.global?.globals?.User || currentState.global?.globals?.App ||
      currentState.global?.globals?.Tenant || currentState.global?.globals?.Environment) {

      // User, App, Tenant, Environment (these are in global.globals)
      const globalItems: Array<keyof typeof currentState.global.globals> = ['User', 'App', 'Tenant', 'Environment'];
      globalItems.forEach(item => {
        if (currentState.global?.globals?.[item]) {
          if (!searchTerm || item.toLowerCase().includes(searchTerm)) {
            matchingGlobalItems.push({ type: 'leaf-item', label: item, path: `global::globals::${item}`, indent: 1 });
          }
        }
      });

      // Other globals items (if any custom globals exist beyond User/App/Tenant/Environment)
      if (hasGlobals) {
        const standardGlobals = new Set(['User', 'App', 'Tenant', 'Environment']);
        Object.keys(globals).forEach(key => {
          if (!standardGlobals.has(key) && (!searchTerm || key.toLowerCase().includes(searchTerm))) {
            matchingGlobalItems.push({ type: 'leaf-item', label: key, path: `global::globals::${key}`, indent: 1 });
          }
        });
      }
    }

    // Only add Global section if there are matching items
    if (matchingGlobalItems.length > 0) {
      tree.push({ type: 'section', label: 'Global', path: 'global', indent: 0 });
      tree.push(...matchingGlobalItems);
    }

    // ========================================
    // SCREENS SECTION (New Tree Structure)
    // ========================================

    // Collect screens items in a temporary array
    const screensSectionItems: TreeItem[] = [];

    // Check if Screen Designer screen exists and has designing screens
    const isScreenDesignerActive = !!currentState[TabCoreScreens.ScreenDesignerScreen];
    const designingScreensData = currentState.designingScreens || {};
    const hasDesigningScreens = Object.keys(designingScreensData).length > 0;

    // Collect all screens (designing if designer is active with designing screens, otherwise runtime)
    const allScreens: Array<{
      id: string;
      name: string;
      isDesigning: boolean;
      queries: any;
      components: any;
      scripts: any;
      metadata: any;
    }> = [];

    // Show designing screens ONLY if Screen Designer screen is active AND has designing screens
    if (isScreenDesignerActive) {
      // Designing Screens
      Object.keys(designingScreensData).forEach(screenId => {
        const screenState = designingScreensData[screenId];
        const screenMetadata = currentState.global?.designingScreens?.[screenId]?.screenMetadata;
        const screenName = screenMetadata?.Name || screenId;

        allScreens.push({
          id: screenId,
          name: screenName,
          isDesigning: true,
          queries: screenState?.queries || {},
          components: screenState?.components || {},
          scripts: {},
          metadata: screenMetadata
        });
      });
    } else {
      // Helper: check if a screen or its root parent is a DevToolScreen
      const isDevToolScreen = (screenId: string): boolean => {
        const visited = new Set<string>();
        let currentId = screenId;

        while (currentId && !visited.has(currentId)) {
          visited.add(currentId);
          const globalScreenData = currentState.global?.screens?.[currentId];
          if (!globalScreenData) break;

          // Check renderer screenRenderingMode (single or array)
          const renderer = globalScreenData.renderer;
          if (renderer) {
            const mode = Array.isArray(renderer)
              ? renderer[0]?.screenRenderingMode
              : (renderer as any)?.screenRenderingMode;
            if (mode === ScreenRenderingMode.DevToolScreen) return true;
          }

          // Trace up the parent chain
          const parentId = globalScreenData.parentScreenId;
          if (!parentId || parentId === currentId) break;
          currentId = parentId;
        }

        return false;
      };

      // Runtime Screens (default behavior)
      const screenIds = Object.keys(currentState).filter(key =>
        key !== 'global' && key !== 'designingScreens'
      );
      screenIds.forEach(screenId => {
        // Skip DevTool internal screens and their children
        if (isDevToolScreen(screenId)) return;

        const screenState = currentState[screenId];
        const screenMetadata = currentState.global?.screens?.[screenId]?.screenMetadata;
        const screenName = screenMetadata?.Name || screenId;

        allScreens.push({
          id: screenId,
          name: screenName,
          isDesigning: false,
          queries: (screenState as any)?.queries || {},
          components: (screenState as any)?.components || {},
          scripts: (screenState as any)?.scripts || {},
          metadata: screenMetadata
        });
      });
    }

    // Build tree for each screen
    allScreens.forEach(screen => {
      const screenPrefix = screen.isDesigning ? 'designing' : 'screen';
      const screenPath = `${screenPrefix}::${screen.id}`;
      const screenLabel = screen.name; // No emoji prefix

      // Check if screen matches search
      let screenMatches = !searchTerm || screen.name.toLowerCase().includes(searchTerm);
      let hasMatchingChildren = false;

      // Collect matching children
      const matchingQueries: any[] = [];
      const matchingComponents: any[] = [];
      const matchingScripts: any[] = [];

      Object.keys(screen.queries).forEach(queryKey => {
        const queryData = screen.queries[queryKey];
        const queryName = queryData?.DisplayName || queryData?.QueryName || queryKey;
        if (!searchTerm || queryName.toLowerCase().includes(searchTerm)) {
          matchingQueries.push({ key: queryKey, name: queryName, data: queryData });
          hasMatchingChildren = true;
        }
      });

      Object.keys(screen.components).forEach(componentKey => {
        if (!searchTerm || componentKey.toLowerCase().includes(searchTerm)) {
          matchingComponents.push({ key: componentKey, name: componentKey });
          hasMatchingChildren = true;
        }
      });

      Object.keys(screen.scripts).forEach(scriptKey => {
        const scriptData = screen.scripts[scriptKey];
        const scriptName = scriptData?.Name || scriptKey;
        if (!searchTerm || scriptName.toLowerCase().includes(searchTerm)) {
          matchingScripts.push({ key: scriptKey, name: scriptName, data: scriptData });
          hasMatchingChildren = true;
        }
      });

      // Only show screen if it matches or has matching children
      if (screenMatches || hasMatchingChildren) {
        // Screen row (clickable + expandable)
        screensSectionItems.push({
          type: 'parent-item',
          label: screenLabel,
          path: screenPath,
          indent: 1,
          hasChildren: matchingQueries.length > 0 || matchingComponents.length > 0 || matchingScripts.length > 0
        });

        // Only show children if expanded
        if (this.isExpanded(screenPath)) {
          // Queries section
          if (matchingQueries.length > 0) {
            const queriesPath = `${screenPath}::queries`;
            screensSectionItems.push({
              type: 'parent-group',
              label: `Queries (${matchingQueries.length})`,
              path: queriesPath,
              indent: 2,
              hasChildren: true
            });

            if (this.isExpanded(queriesPath)) {
              matchingQueries.forEach(query => {
                screensSectionItems.push({
                  type: 'leaf-item',
                  label: query.name,
                  path: `${screenPath}::queries::${query.key}`,
                  indent: 3
                });
              });
            }
          }

          // Components section
          if (matchingComponents.length > 0) {
            const componentsPath = `${screenPath}::components`;
            screensSectionItems.push({
              type: 'parent-group',
              label: `Components (${matchingComponents.length})`,
              path: componentsPath,
              indent: 2,
              hasChildren: true
            });

            if (this.isExpanded(componentsPath)) {
              matchingComponents.forEach(component => {
                screensSectionItems.push({
                  type: 'leaf-item',
                  label: component.name,
                  path: `${screenPath}::components::${component.key}`,
                  indent: 3
                });
              });
            }
          }

          // Scripts section
          if (matchingScripts.length > 0) {
            const scriptsPath = `${screenPath}::scripts`;
            screensSectionItems.push({
              type: 'parent-group',
              label: `Scripts (${matchingScripts.length})`,
              path: scriptsPath,
              indent: 2,
              hasChildren: true
            });

            if (this.isExpanded(scriptsPath)) {
              matchingScripts.forEach(script => {
                screensSectionItems.push({
                  type: 'leaf-item',
                  label: script.name,
                  path: `${screenPath}::scripts::${script.key}`,
                  indent: 3
                });
              });
            }
          }
        }
      }
    });

    // Only add Screens section if there are matching screens
    if (screensSectionItems.length > 0) {
      tree.push({ type: 'section', label: 'Screens', path: 'screens', indent: 0 });
      tree.push(...screensSectionItems);
    }

    return tree;
  });

  selectItem(path: string) {
    this.selectedPath.set(path);
    this.activeTab.set('json');
  }

  // ========================================
  // COMPUTED SIGNALS
  // ========================================

  selectedData = computed(() => {
    const path = this.selectedPath();
    const currentState = this.state();
    if (!path || !currentState) return null;

    const parts = path.split('::');

    // Global paths
    if (parts[0] === 'global') {
      if (parts.length === 1) return currentState.global;

      if (parts[1] === 'globals') {
        if (parts.length === 2) return currentState.global.globals;
        if (parts.length === 3) return currentState.global.globals[parts[2] as keyof typeof currentState.global.globals];
      }
    }

    // Designing screen paths
    if (parts[0] === 'designing') {
      const screenId = parts[1];
      const designingScreensData = currentState.designingScreens || {};
      const screenState = designingScreensData[screenId];
      const globalScreenData = currentState.global?.designingScreens?.[screenId];
      if (!screenState && !globalScreenData) return null;

      // Screen metadata - return the global screen data which has screenId, parentScreenId, form, renderer, etc.
      if (parts.length === 2) {
        return globalScreenData || screenState;
      }

      if (parts[2] === 'queries') {
        const queries = screenState.queries || {};
        if (parts.length === 3) return queries;
        if (parts.length === 4) return queries[parts[3]];
      }

      if (parts[2] === 'components') {
        const components = screenState.components || {};
        if (parts.length === 3) return components;
        if (parts.length === 4) return components[parts[3]];
      }
    }

    // Runtime Screen paths
    if (parts[0] === 'screen') {
      const screenId = parts[1];
      const screenState = currentState[screenId];
      const globalScreenData = currentState.global?.screens?.[screenId];
      if (!screenState && !globalScreenData) return null;

      // Screen metadata - return the global screen data which has screenId, parentScreenId, form, renderer, etc.
      if (parts.length === 2) {
        return globalScreenData || screenState;
      }

      if (parts[2] === 'queries') {
        const queries = (screenState as any).queries || {};
        if (parts.length === 3) return queries;
        if (parts.length === 4) return queries[parts[3]];
      }

      if (parts[2] === 'scripts') {
        const scripts = (screenState as any).scripts || {};
        if (parts.length === 3) return scripts;
        if (parts.length === 4) return scripts[parts[3]];
      }

      if (parts[2] === 'components') {
        const components = (screenState as any).components || {};
        if (parts.length === 3) return components;
        if (parts.length === 4) return components[parts[3]];
      }
    }

    return null;
  });

  selectedDisplayName = computed(() => {
    const path = this.selectedPath();
    const currentState = this.state();
    if (!path || !currentState) return '';

    const parts = path.split('::');

    // Global paths
    if (parts[0] === 'global') {
      if (parts[1] === 'globals' && parts[2]) {
        return parts[2]; // User, App, Tenant, Environment or custom globals
      }
    }

    // Designing screen paths
    if (parts[0] === 'designing') {
      const screenId = parts[1];

      if (parts.length === 2) {
        const screenMetadata = currentState.global?.designingScreens?.[screenId]?.screenMetadata;
        const screenName = screenMetadata?.Name || screenId;
        return screenName;
      }

      const designingScreensData = currentState.designingScreens || {};
      if (parts[2] === 'queries' && parts[3]) {
        const queries = designingScreensData[screenId]?.queries || {};
        const queryData = queries[parts[3]];
        return queryData?.DisplayName || queryData?.QueryName || parts[3];
      }
      if (parts[2] === 'components' && parts[3]) {
        const components = designingScreensData[screenId]?.components || {};
        const component = components[parts[3]];
        const componentType = component?.type || '';
        return `${parts[3]} (${componentType})`;
      }
    }

    // Runtime Screen paths
    if (parts[0] === 'screen') {
      const screenId = parts[1];

      if (parts.length === 2) {
        const screenMetadata = currentState.global?.screens?.[screenId]?.screenMetadata;
        const screenName = screenMetadata?.Name || screenId;
        return screenName;
      }

      if (parts[2] === 'queries' && parts[3]) {
        const queries = (currentState[screenId] as any)?.queries || {};
        const queryData = queries[parts[3]];
        return queryData?.DisplayName || queryData?.QueryName || parts[3];
      }
      if (parts[2] === 'scripts' && parts[3]) {
        const scripts = (currentState[screenId] as any)?.scripts || {};
        return scripts[parts[3]]?.Name || parts[3];
      }
      if (parts[2] === 'components' && parts[3]) {
        const components = (currentState[screenId] as any)?.components || {};
        const component = components[parts[3]];
        const componentType = component?.type || '';
        return `${parts[3]} (${componentType})`;
      }
    }

    return parts[parts.length - 1];
  });

  selectedIcon = computed(() => {
    const path = this.selectedPath();
    if (!path) return '';

    const parts = path.split('::');

    // Global items
    if (parts[0] === 'global') {
      if (parts[1] === 'globals') return 'fa fa-globe';
      return 'fa fa-cube'; // User, App, Tenant, Environment
    }

    // Screen items
    if (parts[0] === 'designing' || parts[0] === 'screen') {
      if (parts.length === 2) return 'fa fa-window-maximize'; // Screen itself
      if (parts[2] === 'queries') return 'fa fa-database';
      if (parts[2] === 'scripts') return 'fa fa-code';
      if (parts[2] === 'components') return 'fa fa-puzzle-piece';
    }

    return 'fa fa-file';
  });

  isSelectedItemQueryComputed = computed(() => {
    const path = this.selectedPath();
    if (!path) return false;
    const parts = path.split('::');
    return (parts[0] === 'screen' || parts[0] === 'designing') && parts[2] === 'queries' && parts.length === 4;
  });

  isSelectedItemScriptComputed = computed(() => {
    const path = this.selectedPath();
    if (!path) return false;
    const parts = path.split('::');
    return parts[0] === 'screen' && parts[2] === 'scripts' && parts.length === 4;
  });

  isSelectedItemScreen = computed(() => {
    const path = this.selectedPath();
    if (!path) return false;
    const parts = path.split('::');
    return (parts[0] === 'screen' || parts[0] === 'designing') && parts.length === 2;
  });

  openScreenInDesigner(): void {
    const path = this.selectedPath();
    if (!path) return;
    const parts = path.split('::');
    const screenId = parts[1];

    const currentPath = window.location.pathname.split('/').slice(0, -1).join('/');
    window.open(`${currentPath}/${Constants.Designer_Tabs_ScreenID}?editScreenId=${screenId}`, '_blank');
  }

  // ========================================
  // TAB SWITCHING & FORM LOADING
  // ========================================

  switchTab(tab: 'json' | 'edit' | 'run'): void {
    this.activeTab.set(tab);

    const timeout = setTimeout(() => {
      if (tab === 'edit') {
        if (this.isSelectedItemQueryComputed() && this.editRenderer) {
          this.loadEditForm();
        } else if (this.isSelectedItemScriptComputed() && this.scriptEditRenderer) {
          this.loadScriptEditForm();
        }
      } else if (tab === 'run' && this.runRenderer) {
        this.loadRunForm();
      }
    }, 100);

    this.timeouts.push(timeout);
  }

  private loadEditForm(): void {
    const queryData = this.selectedData();
    if (!queryData?.ID?.length) return;

    if (!this.editRenderer.rendererService.screenId) {
      this.editRenderer.rendererService.loadFormWithSubmission(
        Constants.Entity_DSQ_Wizard_Detail_ScreenId,
        { Id: queryData.ID }
      );
    }
  }

  private loadRunForm(): void {
    const queryData = this.selectedData();
    if (!queryData?.QueryName?.length) return;

    if (!this.runRenderer.rendererService.screenId) {
      this.runRenderer.rendererService.loadFormWithSubmission(
        TabCoreScreens.TabHTTPRequestScreen,
        { QueryName: queryData.QueryName }
      );
    }
  }

  private loadScriptEditForm(): void {
    const scriptData = this.selectedData();

    if (!this.scriptEditRenderer.rendererService.screenId) {
      this.scriptEditRenderer.rendererService.loadFormWithSubmission(
        Constants.CustomScript_Editor_ScreenId,
        { Id: scriptData.Id }
      );
    }
  }

  // ========================================
  // LIFECYCLE CLEANUP
  // ========================================

  ngOnDestroy(): void {
    if (this.jsonSearchTimeout) {
      clearTimeout(this.jsonSearchTimeout);
      this.jsonSearchTimeout = null;
    }

    this.timeouts.forEach((timeout: any) => clearTimeout(timeout));
    this.timeouts = [];

    this.focusEffectRef?.destroy();
  }
}